import * as yup from "yup";
import { DateTime } from "luxon";

export type AddIntervalFormData = {
  startDate: DateTime;
  endDate: DateTime;
  allDay: boolean;
  absenceNote: string;
};

// An all-day absence runs from the very start of its first day to the last
// minute of its last day - over one day or many.
export const startOfAllDay = (date: DateTime) => date.startOf("day");
export const endOfAllDay = (date: DateTime) =>
  date.set({ hour: 23, minute: 59, second: 0, millisecond: 0 });

// The start and end the absence is saved with. While "All day" is checked the
// pickers only show dates, and whatever time a picked day carries is not
// what the user sees, so the all-day times are applied here.
export const toInterval = (
  data: Pick<AddIntervalFormData, "startDate" | "endDate" | "allDay">
) =>
  data.allDay
    ? {
        startDate: startOfAllDay(data.startDate),
        endDate: endOfAllDay(data.endDate),
      }
    : {
        startDate: data.startDate.startOf("minute"),
        endDate: data.endDate.startOf("minute"),
      };

// The end picker's minDateTime only marks an earlier time as invalid - a typed
// one still reaches the form - so the range is enforced here before saving.
// The absence has to last at least a minute, the precision it is saved with.
const buildAddIntervalSchema = (endNotAfterStartError: string) =>
  yup.object({
    startDate: yup.mixed<DateTime>().required(),
    endDate: yup
      .mixed<DateTime>()
      .required()
      .test("end-after-start", endNotAfterStartError, function (endDate) {
        const { startDate, allDay } = this.parent as AddIntervalFormData;
        if (!startDate || !endDate) return true;
        const interval = toInterval({ startDate, endDate, allDay });
        return interval.endDate.toMillis() > interval.startDate.toMillis();
      }),
    allDay: yup.boolean().required(),
    absenceNote: yup.string().defined(),
  });

export default buildAddIntervalSchema;
